(() => {
  'use strict';
  const root = document.documentElement;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  let manuallyPaused = false;
  let paused = reduced.matches;
  const controllers = new Set();
  const seen = new WeakSet();
  const canObserve = 'IntersectionObserver' in window;
  function applyMotion() {
    paused = manuallyPaused || reduced.matches;
    root.classList.toggle('motion-enabled', !reduced.matches);
    root.classList.toggle('motion-paused', paused);
    document.querySelectorAll('[data-motion-toggle]').forEach(button => {
      button.querySelector('[data-motion-label]').textContent = paused ? 'RESUME MOTION' : 'PAUSE MOTION';
      button.setAttribute('aria-label', paused ? 'Resume page motion' : 'Pause page motion');
      button.disabled = reduced.matches;
      if (reduced.matches) button.querySelector('[data-motion-label]').textContent = 'MOTION REDUCED';
    });
    controllers.forEach(controller => controller.reset());
  }
  function init(scope = document) {
    scope.querySelectorAll('[data-motion-hero]').forEach(hero => {
      if (seen.has(hero)) return;
      seen.add(hero);
      const video = hero.querySelector('video.crew-hero-video');
      if (video) {
        const toggle = hero.querySelector('[data-motion-toggle]');
        toggle.hidden = false;
        video.muted = true;
        video.playsInline = true;
        let onScreen = true, playPending = false, failed = false;
        const permitted = () => !paused && onScreen && !document.hidden && hero.isConnected && !failed;
        const controller = { reset() {
          if (!hero.isConnected) { video.pause(); controllers.delete(controller); return; }
          if (!permitted()) { video.pause(); return; }
          if (!video.paused || playPending) return;
          playPending = true;
          const request = video.play();
          if (request) request.then(() => {
            playPending = false;
            if (!permitted()) video.pause();
          }).catch(() => {
            playPending = false;
            if (permitted()) { manuallyPaused = true; applyMotion(); }
          });
          else playPending = false;
        }};
        controllers.add(controller);
        toggle.addEventListener('click', () => { manuallyPaused = !paused; applyMotion(); });
        video.addEventListener('error', () => {
          failed = true;
          manuallyPaused = true;
          applyMotion();
          toggle.hidden = true;
        });
        if (canObserve) new IntersectionObserver(entries => {
          onScreen = entries[0].isIntersecting;
          controller.reset();
        }, {threshold: 0.15}).observe(hero);
        controller.reset();
        return;
      }
      const scenes = [...hero.querySelectorAll('[data-scene]')];
      const buttons = [...hero.querySelectorAll('[data-scene-select]')];
      let current = 0, timer, onScreen = true, hovered = false;
      const stage = hero.querySelector('.motion-stage');
      hero.querySelector('[data-motion-toggle]').hidden = false;
      hero.querySelector('.scene-controls').hidden = false;
      const show = index => {
        const image = scenes[index]?.querySelector('img');
        if (!image || !image.complete || !image.naturalWidth) return;
        current = index;
        scenes.forEach((scene, i) => { scene.classList.toggle('is-active', i === index); scene.setAttribute('aria-hidden', String(i !== index)); });
        buttons.forEach((button, i) => button.setAttribute('aria-pressed', String(i === index)));
      };
      const controller = { reset() {
        clearInterval(timer);
        if (!hero.isConnected) { controllers.delete(controller); return; }
        stage.classList.toggle('is-out-of-view', !onScreen || document.hidden);
        if (!paused && !hovered && onScreen && !document.hidden) timer = setInterval(() => show((current + 1) % scenes.length), 8000);
      }};
      controllers.add(controller);
      stage.addEventListener('mouseenter', () => { hovered = true; stage.classList.add('is-hovered'); controller.reset(); });
      stage.addEventListener('mouseleave', () => { hovered = false; stage.classList.remove('is-hovered'); controller.reset(); });
      hero.querySelector('[data-motion-toggle]').addEventListener('click', () => { manuallyPaused = !paused; applyMotion(); });
      buttons.forEach((button, index) => button.addEventListener('click', () => { manuallyPaused = true; applyMotion(); show(index); }));
      hero.querySelector('.scene-controls').addEventListener('focusin', () => { manuallyPaused = true; applyMotion(); });
      if (canObserve) new IntersectionObserver(entries => { onScreen = entries[0].isIntersecting; controller.reset(); }, {threshold: 0.15}).observe(hero);
      controller.reset();
    });
    if (canObserve && !reduced.matches) {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => { if (entry.isIntersecting) { entry.target.classList.add('is-visible'); observer.unobserve(entry.target); } });
      }, {threshold: 0.08, rootMargin: '0px 0px 25px 0px'});
      scope.querySelectorAll('.intro .editorial-grid,.range .section-heading,.range-card,.story-content,.process .section-heading,.process-step,.why-heading,.why-item,.contact-heading').forEach((item, index) => {
        if (item.classList.contains('motion-reveal')) return;
        item.style.setProperty('--reveal-delay', `${index % 4 * 70}ms`);
        item.classList.add('motion-reveal');
        observer.observe(item);
      });
    }
    applyMotion();
  }
  document.addEventListener('visibilitychange', () => { root.classList.toggle('motion-suspended', document.hidden); controllers.forEach(controller => controller.reset()); });
  document.addEventListener('shopify:section:unload', () => controllers.forEach(controller => controller.reset()));
  reduced.addEventListener('change', applyMotion);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('focusin', event => { const item = event.target.closest('.motion-reveal'); if (item) item.classList.add('is-visible'); });
  init();
})();
